import { Image, ImageSourcePropType } from 'react-native';

import { isWeb } from 'utils/environment';

import {
  ExtrinsicBoundaryDimensions,
  IntrinsicBoundaryDimensions,
  MeasuredDimensions,
} from './types';

export const isSVG = (format: string | undefined) => format === 'svg';

function deriveHeight({
  aspectRatio,
  width,
}: {
  aspectRatio: number;
  width: number;
}): string | undefined {
  if (!aspectRatio) {
    // lotties don't have an aspect ratio defined, so don't divide by zero
    return undefined;
  }
  return Math.round(width / aspectRatio) + 'px';
}

function deriveWidth({ aspectRatio, height }: { aspectRatio: number; height: number }): string {
  return Math.round(aspectRatio * height) + 'px';
}

export function getBoundaryDimensions({
  extrinsicDimensions,
  intrinsicDimensions,
  measuredDimensions,
}: {
  extrinsicDimensions: ExtrinsicBoundaryDimensions;
  intrinsicDimensions: IntrinsicBoundaryDimensions;
  measuredDimensions: MeasuredDimensions;
}): { width?: string | number; height?: string | number } {
  if (extrinsicDimensions.width && extrinsicDimensions.height) {
    // Both dimensions are explicitly provided. Use them.
    return { width: extrinsicDimensions.width, height: extrinsicDimensions.height };
  }

  if (extrinsicDimensions.height) {
    // Height is explicitly provided. Derive the width.
    return {
      width: deriveWidth({
        aspectRatio: intrinsicDimensions.aspectRatio,
        height:
          typeof extrinsicDimensions.height === 'number'
            ? extrinsicDimensions.height
            : measuredDimensions.height,
      }),
      height: extrinsicDimensions.height,
    };
  }

  return {
    width: extrinsicDimensions.width || measuredDimensions.width || '100%',
    height: deriveHeight({
      aspectRatio: intrinsicDimensions.aspectRatio,
      width:
        typeof extrinsicDimensions.width === 'number'
          ? extrinsicDimensions.width
          : measuredDimensions.width,
    }),
  };
}

/** cross platform get image width/height */
export const getImageWidthHeight = (asset?: ImageSourcePropType) => {
  let width: number | undefined = undefined;
  let height: number | undefined = undefined;

  if (!asset) {
    return { width, height };
  }

  if (isWeb) {
    const webAsset = Array.isArray(asset) ? asset[0] : asset;
    if (webAsset && typeof webAsset === 'object' && 'width' in webAsset && 'height' in webAsset) {
      width = (webAsset as { width?: number }).width;
      height = (webAsset as { height?: number }).height;
    }
  } else {
    const result = Image.resolveAssetSource(asset);
    width = result?.width;
    height = result?.height;
  }

  return { height, width };
};

/** get aspect ratio of image to set height dynamically if you are using 100% width and don't have a fixed height container */
export const getImageAspectRatio = (asset?: ImageSourcePropType) => {
  const { width, height } = getImageWidthHeight(asset);

  if (!width || !height) {
    return undefined;
  }
  return width / height;
};
